import React, { memo } from 'react';

import _truncate from 'lodash/truncate';

const DIRECT_URL_TYPE = 'DirectURL';

/**
 * "Review and send" preview shown when the user clicks Preview on the compose form.
 * Matches the Braze Design review frame (Figma 199-286):
 *   - "What readers will see:" caption
 *   - a purple gradient stage holding the iOS + Android notification cards
 *   - ARTICLE / Direct URL link row
 *   - BRAZE AUDIENCE SEGMENTS pills (one per selected segment; breadcrumb labels)
 *
 * The image URL passed in is the *effective* URL — same one FCM + Braze receive —
 * so the preview is WYSIWYG.
 *
 * Props
 *   selectedCampaigns: [{ id, name }] — the chosen audience segments
 *   title, body:       strings rendered into both device cards
 *   typeNotification:  'Article' | 'DirectURL'
 *   deepLink:          string — story_url (Article) or directUrl.value (DirectURL)
 *   showImage:         boolean — gated by isIncludeImage + hasEffectiveImageUrl
 *   previewImageUrl:   string — the effective image URL
 */
const PreviewView = ({
  selectedCampaigns,
  title,
  body,
  typeNotification,
  deepLink,
  showImage,
  previewImageUrl,
}) => {
  const linkLabel = typeNotification === DIRECT_URL_TYPE ? 'Direct URL' : 'Article';
  const hasImage = showImage && Boolean(previewImageUrl);

  return (
    <div className='preview-review'>
      <div className='preview-review__caption'>What readers will see:</div>

      <div className='preview-stage'>
        <div className='preview-stage__device'>
          <div className='preview-stage__label'>iOS</div>
          <div className='ntf ntf--ios'>
            <div className='ntf__icon'>I</div>
            <div className='ntf__body'>
              <div className='ntf__title'>{title}</div>
              <div className='ntf__msg'>{body}</div>
            </div>
            <div className='ntf__aside'>
              <div className='ntf__time'>now</div>
              {hasImage && <img className='ntf__thumb' src={previewImageUrl} alt='' />}
            </div>
          </div>
        </div>

        <div className='preview-stage__device'>
          <div className='preview-stage__label'>Android</div>
          <div className='ntf ntf--android'>
            <div className='ntf__icon ntf__icon--android'>I</div>
            <div className='ntf__body'>
              <div className='ntf__title'>
                {title}
                <span className='ntf__meta'> · now</span>
              </div>
              <div className='ntf__msg'>{body}</div>
            </div>
            {hasImage && <img className='ntf__thumb' src={previewImageUrl} alt='' />}
            <span className='ntf__chevron' aria-hidden='true'>⌄</span>
          </div>
        </div>
      </div>

      {deepLink && (
        <div className='preview-review__row'>
          <span className='preview-review__row-label'>{linkLabel}:</span>
          <a
            href={deepLink}
            target='_blank'
            rel='noopener noreferrer'
            className='preview-review__link'
          >
            {_truncate(deepLink, { length: 70 })}
          </a>
        </div>
      )}

      {selectedCampaigns.length > 0 && (
        <div className='preview-review__row preview-review__row--segments'>
          <span className='preview-review__row-label'>Braze audience segments:</span>
          <div className='preview-review__pills'>
            {selectedCampaigns.map((c) => (
              <span key={c.id} className='preview-review__pill'>{c.name}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(PreviewView);
